import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { money, statusBadge } from "../utils/order";

import OrderService from "../services/order.service";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Spinner from "./Spinner";

const OrderDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  const load = () => {
    setLoading(true);
    OrderService.get(id)
      .then((res) => setOrder(res.data))
      .catch((err) =>
        setError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancel = () => {
    if (!window.confirm(t("orderDetail.confirmCancel"))) return;
    setWorking(true);
    OrderService.cancel(order.id)
      .then((res) => {
        setOrder(res.data);
        toast.success(t("orderDetail.cancelled"));
      })
      .catch((err) =>
        toast.error(
          (err.response && err.response.data && err.response.data.message) ||
            err.message
        )
      )
      .finally(() => setWorking(false));
  };

  if (loading) {
    return (
      <Spinner />
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <Link to="/orders" className="btn btn-secondary">{t("orderDetail.backToOrders")}</Link>
      </div>
    );
  }

  const meta = statusBadge(order.status);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{t("orderDetail.title", { id: order.id })}</h3>
        <span className={`badge fs-6 ${meta.cls}`}>{t(`orderStatus.${order.status}`, meta.label)}</span>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card card-body h-100">
            <h6 className="text-muted">{t("orderDetail.shipping")}</h6>
            <div><strong>{order.customer_name}</strong></div>
            <div>{order.phone}</div>
            <div>{order.shipping_address}</div>
            {order.note && <div className="mt-2 text-muted">{t("orderDetail.note", { note: order.note })}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="card card-body h-100">
            <h6 className="text-muted">{t("orderDetail.orderInfo")}</h6>
            <div>{t("orderDetail.placed", { date: new Date(order.createdAt).toLocaleString() })}</div>
            <div>{t("orderDetail.lastUpdate", { date: new Date(order.updatedAt).toLocaleString() })}</div>
            {order.user && <div>{t("orderDetail.customer", { username: order.user.username, email: order.user.email })}</div>}
          </div>
        </div>
      </div>

      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th>{t("orderDetail.product")}</th>
            <th style={{ width: 120 }}>{t("orderDetail.unitPrice")}</th>
            <th style={{ width: 90 }}>{t("orderDetail.qty")}</th>
            <th style={{ width: 130 }}>{t("orderDetail.lineTotal")}</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it) => (
            <tr key={it.id}>
              <td>{it.product_name}</td>
              <td>{money(it.unit_price)}</td>
              <td>{it.quantity}</td>
              <td className="fw-bold">{money(it.line_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={3} className="text-end">{t("orderDetail.total")}</th>
            <th className="text-primary">{money(order.total_amount)}</th>
          </tr>
        </tfoot>
      </table>

      <div className="d-flex gap-2">
        <Link to="/orders" className="btn btn-outline-secondary">{t("orderDetail.backToOrders")}</Link>
        {order.status === "pending" && (
          <button className="btn btn-outline-danger" onClick={cancel} disabled={working}>
            {t("orderDetail.cancelOrder")}
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
